// Weekly-summary tile — Session 7 PR4.
//
// Surfaces the most recent non-dismissed weekly_summaries row on Home. Hides
// itself when:
//   - the latest row's `dismissed_at` is set (don't show stale dismissed
//     content; the user already opted out for this week)
//   - there are no rows at all (cron hasn't fired yet — empty Home is
//     better than a "nothing yet" placeholder)
//
// When it does render: narrative on top, week-over-week delta chips below
// (interviews / new applications / messages sent), then dismiss + regenerate
// controls. Regenerate is available to everyone (the route is per-user).

import { ArrowDownRight, ArrowUpRight, Minus, RefreshCw, X } from "lucide-react";
import { Card, CardBody, CardHeader } from "../ui/Card";
import { Button } from "../ui/Button";
import { Skeleton } from "../ui/Skeleton";
import {
  useDismissWeeklySummary,
  useRegenerateWeeklySummary,
  useWeeklySummaries,
  type WeeklySummary,
} from "../../lib/weekly_summaries";

export function WeeklySummaryTile() {
  const summaries = useWeeklySummaries();

  if (summaries.isLoading) {
    return (
      <Card>
        <CardHeader title="This week" />
        <CardBody className="space-y-2">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-11/12" />
          <Skeleton className="h-3 w-3/4" />
        </CardBody>
      </Card>
    );
  }

  // Don't surface a broken tile on a Home dashboard. The other tiles still
  // work; the user can hit /weekly-summaries directly if they want a retry.
  if (summaries.error) return null;

  const latest = summaries.data?.summaries[0];
  if (!latest) return null;
  if (latest.dismissed_at !== null) return null;

  return <WeeklySummaryCard summary={latest} />;
}

function WeeklySummaryCard({ summary }: { summary: WeeklySummary }) {
  const dismiss = useDismissWeeklySummary();
  const regenerate = useRegenerateWeeklySummary();
  const weekLabel = new Date(summary.week_start).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
  const stats = summary.stats;

  return (
    <Card>
      <CardHeader
        title={`Week of ${weekLabel}`}
        description="Generated Monday morning. Dismiss if you've read it."
        action={
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => regenerate.mutate()}
              loading={regenerate.isPending}
              title="Regenerate from latest data"
              aria-label="Regenerate weekly summary"
            >
              <RefreshCw size={13} aria-hidden />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => dismiss.mutate(summary.id)}
              loading={dismiss.isPending}
              title="Dismiss until next week"
              aria-label="Dismiss weekly summary"
            >
              <X size={13} aria-hidden />
            </Button>
          </div>
        }
      />
      <CardBody className="space-y-3">
        <p className="whitespace-pre-line text-sm leading-relaxed text-neutral-200">
          {summary.narrative}
        </p>
        {stats ? <DeltaChips stats={stats} /> : null}
      </CardBody>
    </Card>
  );
}

function DeltaChips({
  stats,
}: {
  stats: NonNullable<WeeklySummary["stats"]>;
}) {
  const chips: Array<{ label: string; value: number; delta: number }> = [
    {
      label: "interviews",
      value: stats.this_week.interviews_added,
      delta: stats.this_week.interviews_added - stats.prev_week.interviews_added,
    },
    {
      label: "new apps",
      value: stats.this_week.new_applications,
      delta: stats.this_week.new_applications - stats.prev_week.new_applications,
    },
    {
      label: "messages",
      value: stats.this_week.messages_sent,
      delta: stats.this_week.messages_sent - stats.prev_week.messages_sent,
    },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2 pt-1">
      {chips.map((chip) => (
        <DeltaChip
          key={chip.label}
          label={chip.label}
          value={chip.value}
          delta={chip.delta}
        />
      ))}
      <span className="ml-auto text-[10px] uppercase tracking-wider text-neutral-500">
        {stats.stuck_count} stuck · {stats.progressed_count} progressed
      </span>
    </div>
  );
}

function DeltaChip({
  label,
  value,
  delta,
}: {
  label: string;
  value: number;
  delta: number;
}) {
  const Icon = delta > 0 ? ArrowUpRight : delta < 0 ? ArrowDownRight : Minus;
  const tone =
    delta > 0
      ? "text-emerald-400"
      : delta < 0
        ? "text-amber-400"
        : "text-neutral-500";
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-neutral-800 bg-neutral-900/40 px-2 py-1 text-xs">
      <span className="font-medium tabular-nums text-neutral-100">{value}</span>
      <span className="text-neutral-500">{label}</span>
      <Icon size={11} aria-hidden className={tone} />
      <span className={`tabular-nums ${tone}`}>
        {delta > 0 ? `+${delta}` : delta}
      </span>
    </span>
  );
}
